import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  createConversation,
  fetchAIContext,
  getOnboardingCoverage,
} from "../api";
import ConversationView from "../ConversationView";

// Full-screen conversation view that walks a fresh seller through the
// onboarding facts Qada needs before valuation. No sidebar. Progress
// indicator at the top reflects CompanyIntelligence coverage — a
// deterministic percentage the backend computes from the current
// snapshot's known-vs-tracked fact ratio.
//
// A single conversation is auto-created on mount and tagged in its
// title so the setup thread is easy to find later in the workspace.
// Coverage is polled on mount and then re-fetched every 6s while the
// user is on this screen — cheap, and it keeps the number honest
// without needing an SSE side-channel for facts the tool layer
// already writes.
export default function DealSetup() {
  const { id: workspaceIdRaw } = useParams();
  const workspaceId = Number(workspaceIdRaw);
  const navigate = useNavigate();

  const [ctx, setCtx] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [creating, setCreating] = useState(true);
  const [error, setError] = useState(null);

  const [coverage, setCoverage] = useState({
    coverage_pct: 0,
    known: 0,
    tracked: 0,
  });

  // Guard StrictMode double-mount from creating two setup conversations.
  const bootRef = useRef(false);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;

    let alive = true;
    (async () => {
      try {
        const context = await fetchAIContext();
        if (!alive) return;
        setCtx(context);

        // Confirm the workspace id in the URL matches something the
        // caller actually owns. If not, drop them at /onboard so they
        // can start over cleanly.
        const owned = (context.workspaces || []).some(
          (w) => w.id === workspaceId,
        );
        if (!owned) {
          navigate("/onboard", { replace: true });
          return;
        }

        const orgName =
          context.workspaces.find((w) => w.id === workspaceId)
            ?.organisation_name || "Ny affär";
        const conv = await createConversation({
          workspaceId,
          title: `Onboarding: ${orgName}`,
        });
        if (!alive) return;
        setConversationId(conv.id);
      } catch (e) {
        if (!alive) return;
        setError(e?.body?.detail || e.message || "Kunde inte starta konversationen.");
      } finally {
        if (alive) setCreating(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [workspaceId, navigate]);

  // Coverage polling. Simple setInterval — the endpoint is deterministic
  // and cheap. Refetched every 6s while mounted.
  const refetchCoverage = useCallback(async () => {
    try {
      const c = await getOnboardingCoverage(workspaceId);
      setCoverage(c);
    } catch {
      /* non-fatal — bar stays at last known value */
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) return;
    refetchCoverage();
    const t = setInterval(refetchCoverage, 6000);
    return () => clearInterval(t);
  }, [workspaceId, refetchCoverage]);

  const done = coverage.coverage_pct >= 100;

  return (
    <div className="qai-setup-shell">
      <header className="qai-setup-header">
        <div className="qai-setup-brand">
          <span className="qai-brand-mark">Q</span>
          <span>Onboarding</span>
        </div>
        <div className="qai-setup-progress">
          <div className="qai-setup-progress-track">
            <div
              className="qai-setup-progress-fill"
              style={{ width: `${Math.min(100, coverage.coverage_pct)}%` }}
            />
          </div>
          <div className="qai-setup-progress-meta">
            <span>{coverage.coverage_pct}% klart</span>
            <span className="qai-setup-progress-detail">
              {coverage.known} av {coverage.tracked} fält ifyllda
            </span>
          </div>
        </div>
        {done && (
          <button
            type="button"
            className="qai-onboard-primary"
            onClick={() => navigate(`/deal/${workspaceId}`)}
          >
            Fortsätt till värdering →
          </button>
        )}
      </header>

      <main className="qai-setup-main">
        {creating && (
          <div className="qai-thread-loading">
            Startar Sigge…
          </div>
        )}
        {error && !creating && (
          <div className="qai-error">{error}</div>
        )}
        {conversationId && (
          <ConversationView
            key={conversationId}
            conversationId={conversationId}
            hideHeader
            onConversationChanged={refetchCoverage}
          />
        )}
      </main>
    </div>
  );
}
